/**
 * Sprint S7 — admin-only "Find user by QolSync ID" panel.
 *
 * Calls GET /platform/users/by-handle/{handle} (AdminUserLookupFunctions) and
 * renders the sanitized user + tenant pair. Support agents land here from
 * customer email/chat where the user has quoted their QolSync handle.
 *
 * Form input is trimmed and uppercased before submit because handles are
 * stored uppercase by `QolsyncHandleAllocator` — pasting `q-ab12c3` from a
 * lowercased email transcript should still resolve. The backend does the
 * same normalization defensively, but doing it on the client avoids one
 * round-trip and shows the user the canonical form before they submit.
 */

import { useState } from 'react';
import { Search, AlertCircle, CheckCircle, Loader2, Copy } from 'lucide-react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { trackException, trackEvent } from '../services/appInsightsService';
import Button from './ui/Button';
import Input from './ui/Input';

const FUNCTIONS_URL =
    process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';

export default function FindUserByHandle() {
    const [handle, setHandle] = useState('');
    const [result, setResult] = useState(null);
    const [status, setStatus] = useState('idle'); // idle | loading | found | not_found | error
    const [errorMsg, setErrorMsg] = useState(null);

    async function handleSubmit(e) {
        e.preventDefault();
        const normalized = handle.trim().toUpperCase();
        if (!normalized) {
            setStatus('error');
            setErrorMsg('Enter a QolSync ID (e.g. Q-AB12C3)');
            return;
        }

        setStatus('loading');
        setResult(null);
        setErrorMsg(null);

        try {
            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}/platform/users/by-handle/${encodeURIComponent(normalized)}`
            );
            if (response.status === 404) {
                setStatus('not_found');
                trackEvent('AdminPortal.FindByHandle.NotFound', { handle: normalized });
                return;
            }
            if (response.status === 401 || response.status === 403) {
                setStatus('error');
                setErrorMsg('You do not have permission to look up users by handle.');
                return;
            }
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                setStatus('error');
                setErrorMsg(body.error || `Lookup failed (HTTP ${response.status})`);
                return;
            }
            const body = await response.json();
            setResult(body);
            setStatus('found');
            trackEvent('AdminPortal.FindByHandle.Found', {
                handle: normalized,
                tenantId: body?.tenant?.tenantId
            });
        } catch (err) {
            console.error('[FindUserByHandle] lookup failed:', err);
            trackException(err, { errorType: 'AdminPortal_FindByHandle_NetworkError' });
            setStatus('error');
            setErrorMsg('Network error — check your connection and retry.');
        }
    }

    function handleCopyId(value) {
        if (!value) return;
        navigator.clipboard?.writeText(value);
    }

    return (
        <section className="find-user-by-handle" data-testid="find-user-by-handle">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Search size={20} aria-hidden="true" />
                Find user by QolSync ID
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: 0, marginBottom: 16 }}>
                Look up a user account by the permanent <code>Q-XXXXXX</code> handle
                they quote from their iOS Settings or HomePage Profile page.
            </p>

            <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}
                data-testid="find-user-by-handle-form"
            >
                <Input
                    id="handle-input"
                    label="QolSync ID"
                    value={handle}
                    onChange={(e) => setHandle(e.target.value)}
                    placeholder="Q-AB12C3"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck="false"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                    data-testid="find-user-handle-input"
                />
                <Button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid="find-user-handle-submit"
                >
                    {status === 'loading' ? (
                        <>
                            <Loader2 size={16} className="spin" aria-hidden="true" /> Looking up…
                        </>
                    ) : (
                        <>
                            <Search size={16} aria-hidden="true" /> Look up
                        </>
                    )}
                </Button>
            </form>

            {status === 'error' && errorMsg && (
                <div
                    role="alert"
                    data-testid="find-user-error"
                    style={{
                        display: 'flex', gap: 8, alignItems: 'center',
                        padding: 12, borderRadius: 8,
                        background: 'var(--bg-error, #fef2f2)',
                        color: 'var(--text-error, #991b1b)',
                        marginBottom: 16
                    }}
                >
                    <AlertCircle size={16} aria-hidden="true" /> {errorMsg}
                </div>
            )}

            {status === 'not_found' && (
                <div
                    role="status"
                    data-testid="find-user-not-found"
                    style={{
                        display: 'flex', gap: 8, alignItems: 'center',
                        padding: 12, borderRadius: 8,
                        background: 'var(--bg-warning, #fefce8)',
                        color: 'var(--text-warning, #854d0e)',
                        marginBottom: 16
                    }}
                >
                    <AlertCircle size={16} aria-hidden="true" />
                    No user found for that QolSync ID. Confirm the spelling — characters
                    <code> 0/O</code> and <code>1/I/L</code> are not used in handles.
                </div>
            )}

            {status === 'found' && result && (
                <div
                    data-testid="find-user-result"
                    style={{
                        padding: 16, borderRadius: 8,
                        background: 'var(--bg-card, #f8fafc)',
                        border: '1px solid var(--border-color, #e2e8f0)'
                    }}
                >
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        marginBottom: 12,
                        color: 'var(--text-success, #166534)'
                    }}>
                        <CheckCircle size={16} aria-hidden="true" />
                        <strong data-testid="find-user-result-handle">{result.handle}</strong>
                    </div>

                    <dl style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 1fr',
                        rowGap: 8, columnGap: 12,
                        margin: 0
                    }}>
                        <dt>User ID</dt>
                        <dd style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'monospace', margin: 0 }}>
                            <span data-testid="find-user-result-userid">{result.user?.userId}</span>
                            <button
                                type="button"
                                onClick={() => handleCopyId(result.user?.userId)}
                                aria-label="Copy user ID"
                                style={{
                                    background: 'transparent', border: 'none',
                                    cursor: 'pointer', padding: 2,
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                <Copy size={14} />
                            </button>
                        </dd>

                        <dt>Email</dt>
                        <dd data-testid="find-user-result-email" style={{ margin: 0 }}>
                            {result.user?.email || '—'}
                        </dd>

                        <dt>Name</dt>
                        <dd style={{ margin: 0 }}>{result.user?.name || result.user?.displayName || '—'}</dd>

                        <dt>Role</dt>
                        <dd style={{ margin: 0 }}>{result.user?.role || '—'}</dd>

                        <dt>Status</dt>
                        <dd style={{ margin: 0 }}>{result.user?.status || '—'}</dd>

                        <dt>Tenant</dt>
                        <dd data-testid="find-user-result-tenant" style={{ margin: 0 }}>
                            {result.tenant?.tenantName || result.tenant?.tenantId || '—'}
                        </dd>

                        <dt>Plan tier</dt>
                        <dd data-testid="find-user-result-plan" style={{ margin: 0 }}>
                            {result.tenant?.planTier || '—'}
                        </dd>
                    </dl>
                </div>
            )}
        </section>
    );
}
